import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Stack, Tab, Tabs } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { OCPP } from "../env";
import { useAuthApi } from "../hooks/useAuthApi";
import type {
  Charger,
  FirmwareArtifact,
  FirmwareModelMapping,
  FirmwareUpstream,
  MaintenanceAlert,
  MaintenanceTicket,
  MaintenanceWebhookSubscription,
} from "../types";
import { useToast } from "../toast";
import { AlertsCard } from "./maintenance/components/AlertsCard";
import { DiagnosticsCard } from "./maintenance/components/DiagnosticsCard";
import { FirmwareManagementCard } from "./maintenance/components/FirmwareManagementCard";
import { FirmwareUpstreamsCard } from "./maintenance/components/FirmwareUpstreamsCard";
import { ModelMappingsCard } from "./maintenance/components/ModelMappingsCard";
import { TicketsCard } from "./maintenance/components/TicketsCard";
import { WebhooksCard } from "./maintenance/components/WebhooksCard";
import type {
  DiagnosticsDownloadUrlResponse,
  DiagnosticsUploadUrlResponse,
  FirmwareDeployResponse,
  FirmwareUploadResponse,
} from "./maintenance/types";

type MaintenanceTab = "monitoring" | "firmware" | "diagnostics" | "integrations";

export default function Maintenance() {
  const api = useAuthApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<MaintenanceTab>("monitoring");
  const [firmwares, setFirmwares] = useState<FirmwareArtifact[]>([]);
  const [upstreams, setUpstreams] = useState<FirmwareUpstream[]>([]);
  const [modelMappings, setModelMappings] = useState<FirmwareModelMapping[]>([]);
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [alerts, setAlerts] = useState<MaintenanceAlert[]>([]);
  const [tickets, setTickets] = useState<MaintenanceTicket[]>([]);
  const [webhooks, setWebhooks] = useState<MaintenanceWebhookSubscription[]>([]);
  const [newUpstreamVendor, setNewUpstreamVendor] = useState("");
  const [newUpstreamName, setNewUpstreamName] = useState("");
  const [newUpstreamSourceUrl, setNewUpstreamSourceUrl] = useState("");
  const [mappingVendor, setMappingVendor] = useState("");
  const [mappingModel, setMappingModel] = useState("");
  const [mappingUpstreamId, setMappingUpstreamId] = useState<number | "">("");
  const [uploadUpstreamId, setUploadUpstreamId] = useState<number | "">("");
  const [uploadVersion, setUploadVersion] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedFirmwareId, setSelectedFirmwareId] = useState<number | "">("");
  const [selectedChargePoints, setSelectedChargePoints] = useState<string[]>([]);
  const [deployRetries, setDeployRetries] = useState<number | "">("");
  const [deployRetryInterval, setDeployRetryInterval] = useState<number | "">("");
  const [deployResult, setDeployResult] = useState<FirmwareDeployResponse | null>(null);
  const [diagnosticChargePoint, setDiagnosticChargePoint] = useState<string>("");
  const [diagnosticsDownloadKey, setDiagnosticsDownloadKey] = useState<string | null>(null);
  const [diagnosticsDownloadFileName, setDiagnosticsDownloadFileName] = useState<string | null>(null);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");

  const onlineChargers = useMemo(
    () => chargers.filter((c) => c.is_connected).sort((a, b) => a.charge_point_id.localeCompare(b.charge_point_id)),
    [chargers]
  );
  const openTickets = useMemo(
    () => tickets.filter((ticket) => ticket.status !== "resolved" && ticket.status !== "closed").length,
    [tickets]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fw, ups, mappings, cp, activeAlerts, maintenanceTickets, webhookConfigs] = await Promise.all([
        api<FirmwareArtifact[]>(`${OCPP}/maintenance/firmware`),
        api<FirmwareUpstream[]>(`${OCPP}/maintenance/firmware/upstreams`),
        api<FirmwareModelMapping[]>(`${OCPP}/maintenance/firmware/model-mappings`),
        api<Charger[]>(`${OCPP}/chargers`),
        api<MaintenanceAlert[]>(`${OCPP}/maintenance/alerts`),
        api<MaintenanceTicket[]>(`${OCPP}/maintenance/tickets`),
        api<MaintenanceWebhookSubscription[]>(`${OCPP}/maintenance/webhooks`),
      ]);
      setFirmwares(fw ?? []);
      setUpstreams(ups ?? []);
      setModelMappings(mappings ?? []);
      setChargers(cp ?? []);
      setAlerts(activeAlerts ?? []);
      setTickets(maintenanceTickets ?? []);
      setWebhooks(webhookConfigs ?? []);
      setDiagnosticChargePoint((current) => current || (cp?.[0]?.charge_point_id ?? ""));
      setUploadUpstreamId((current) => current || (ups?.[0]?.id ?? ""));
      setMappingUpstreamId((current) => current || (ups?.[0]?.id ?? ""));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadFirmware = async () => {
    if (!uploadUpstreamId || !uploadVersion || !uploadFile) {
      toast.show("Select upstream, firmware version, and file", "error");
      return;
    }
    try {
      const response = await api<FirmwareUploadResponse>(`${OCPP}/maintenance/firmware/upload-url`, {
        method: "POST",
        body: JSON.stringify({
          upstream_id: uploadUpstreamId,
          version: uploadVersion.trim(),
          file_name: uploadFile.name,
          content_type: uploadFile.type || "application/octet-stream",
        }),
      });
      if (!response) throw new Error("No upload URL returned");

      const uploadResponse = await fetch(response.upload_url, {
        method: "PUT",
        headers: { "Content-Type": uploadFile.type || "application/octet-stream" },
        body: uploadFile,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed (${uploadResponse.status})`);
      }
      toast.show(`Uploaded firmware ${response.firmware.version}`);
      setUploadFile(null);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const createUpstream = async () => {
    if (!newUpstreamVendor || !newUpstreamName) {
      toast.show("Provide upstream vendor and name", "error");
      return;
    }
    try {
      await api(`${OCPP}/maintenance/firmware/upstreams`, {
        method: "POST",
        body: JSON.stringify({
          vendor: newUpstreamVendor.trim(),
          name: newUpstreamName.trim(),
          source_url: newUpstreamSourceUrl.trim() || null,
        }),
      });
      toast.show("Firmware upstream created");
      setNewUpstreamVendor("");
      setNewUpstreamName("");
      setNewUpstreamSourceUrl("");
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const upsertModelMapping = async () => {
    if (!mappingVendor || !mappingModel || !mappingUpstreamId) {
      toast.show("Provide vendor, model, and upstream", "error");
      return;
    }
    try {
      await api(`${OCPP}/maintenance/firmware/model-mappings`, {
        method: "POST",
        body: JSON.stringify({
          vendor: mappingVendor.trim(),
          model: mappingModel.trim(),
          upstream_id: mappingUpstreamId,
        }),
      });
      toast.show("Model-to-upstream mapping saved");
      setMappingVendor("");
      setMappingModel("");
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const deployFirmware = async () => {
    if (!selectedFirmwareId) {
      toast.show("Select a firmware version", "error");
      return;
    }
    try {
      const result = await api<FirmwareDeployResponse>(`${OCPP}/maintenance/firmware/deploy`, {
        method: "POST",
        body: JSON.stringify({
          firmware_id: selectedFirmwareId,
          charge_point_ids: selectedChargePoints.length > 0 ? selectedChargePoints : null,
          retries: deployRetries === "" ? null : deployRetries,
          retry_interval: deployRetryInterval === "" ? null : deployRetryInterval,
        }),
      });
      setDeployResult(result);
      toast.show("Firmware deployment request sent");
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const requestDiagnostics = async () => {
    const charger = chargers.find((item) => item.charge_point_id === diagnosticChargePoint);
    if (!charger || !charger.tenant_slug) {
      toast.show("Select a valid charger", "error");
      return;
    }
    try {
      const now = new Date();
      const logFile = `${charger.charge_point_id}-${now.toISOString().replace(/[:.]/g, "-")}.log`;
      const upload = await api<DiagnosticsUploadUrlResponse>(`${OCPP}/maintenance/diagnostics/upload-url`, {
        method: "POST",
        body: JSON.stringify({
          charge_point_id: charger.charge_point_id,
          file_name: logFile,
          content_type: "text/plain",
        }),
      });
      if (!upload) throw new Error("Failed to create diagnostics upload URL");

      if (charger.ocpp_version === "2.0.1") {
        await api(
          `${OCPP}/tenants/${charger.tenant_slug}/chargers/${charger.charge_point_id}/actions/get-log`,
          {
            method: "POST",
            body: JSON.stringify({ remote_location: upload.upload_url }),
          }
        );
      } else {
        await api(
          `${OCPP}/tenants/${charger.tenant_slug}/chargers/${charger.charge_point_id}/actions/get-diagnostics`,
          {
            method: "POST",
            body: JSON.stringify({ location: upload.upload_url }),
          }
        );
      }
      setDiagnosticsDownloadKey(upload.key);
      setDiagnosticsDownloadFileName(logFile);
      toast.show("Diagnostics requested. Use the download button when the file is ready.");
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const downloadDiagnosticsToLocal = async () => {
    if (!diagnosticsDownloadKey) {
      toast.show("Request diagnostics first", "error");
      return;
    }
    try {
      const response = await api<DiagnosticsDownloadUrlResponse>(`${OCPP}/maintenance/diagnostics/download-url`, {
        method: "POST",
        body: JSON.stringify({
          key: diagnosticsDownloadKey,
          file_name: diagnosticsDownloadFileName ?? undefined,
        }),
      });
      if (!response) throw new Error("Failed to create diagnostics download URL");
      const link = document.createElement("a");
      link.href = response.download_url;
      link.download = diagnosticsDownloadFileName ?? "diagnostics.log";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const createWebhook = async () => {
    if (!newWebhookUrl.trim()) {
      toast.show("Provide a webhook target URL", "error");
      return;
    }
    try {
      await api(`${OCPP}/maintenance/webhooks`, {
        method: "POST",
        body: JSON.stringify({ target_url: newWebhookUrl.trim() }),
      });
      toast.show("Maintenance webhook created");
      setNewWebhookUrl("");
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${onlineChargers.length} online chargers`} color={onlineChargers.length > 0 ? "success" : "default"} />
          <Chip label={`${alerts.length} active alerts`} color={alerts.length > 0 ? "warning" : "default"} />
          <Chip label={`${openTickets} open tickets`} color={openTickets > 0 ? "error" : "default"} />
          <Chip label={`${webhooks.length} webhooks`} color={webhooks.length > 0 ? "info" : "default"} />
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(_, value: MaintenanceTab) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`Monitoring (${alerts.length + openTickets})`} value="monitoring" />
          <Tab label={`Firmware (${firmwares.length})`} value="firmware" />
          <Tab label="Diagnostics" value="diagnostics" />
          <Tab label={`Integrations (${webhooks.length})`} value="integrations" />
        </Tabs>
      </Box>

      {activeTab === "monitoring" && (
        <Stack spacing={2}>
          <AlertsCard loading={loading} alerts={alerts} />
          <TicketsCard loading={loading} tickets={tickets} />
        </Stack>
      )}

      {activeTab === "firmware" && (
        <Stack spacing={2}>
          <FirmwareUpstreamsCard
            upstreams={upstreams}
            newUpstreamVendor={newUpstreamVendor}
            newUpstreamName={newUpstreamName}
            newUpstreamSourceUrl={newUpstreamSourceUrl}
            setNewUpstreamVendor={setNewUpstreamVendor}
            setNewUpstreamName={setNewUpstreamName}
            setNewUpstreamSourceUrl={setNewUpstreamSourceUrl}
            onCreateUpstream={createUpstream}
          />

          <ModelMappingsCard
            modelMappings={modelMappings}
            upstreams={upstreams}
            mappingVendor={mappingVendor}
            mappingModel={mappingModel}
            mappingUpstreamId={mappingUpstreamId}
            setMappingVendor={setMappingVendor}
            setMappingModel={setMappingModel}
            setMappingUpstreamId={setMappingUpstreamId}
            onSaveMapping={upsertModelMapping}
          />

          <FirmwareManagementCard
            firmwares={firmwares}
            upstreams={upstreams}
            onlineChargers={onlineChargers}
            uploadUpstreamId={uploadUpstreamId}
            uploadVersion={uploadVersion}
            uploadFile={uploadFile}
            selectedFirmwareId={selectedFirmwareId}
            selectedChargePoints={selectedChargePoints}
            deployRetries={deployRetries}
            deployRetryInterval={deployRetryInterval}
            deployResult={deployResult}
            setUploadUpstreamId={setUploadUpstreamId}
            setUploadVersion={setUploadVersion}
            setUploadFile={setUploadFile}
            setSelectedFirmwareId={setSelectedFirmwareId}
            setSelectedChargePoints={setSelectedChargePoints}
            setDeployRetries={setDeployRetries}
            setDeployRetryInterval={setDeployRetryInterval}
            onUploadFirmware={uploadFirmware}
            onDeployFirmware={deployFirmware}
          />
        </Stack>
      )}

      {activeTab === "diagnostics" && (
        <DiagnosticsCard
          chargers={chargers}
          diagnosticChargePoint={diagnosticChargePoint}
          canDownloadDiagnostics={Boolean(diagnosticsDownloadKey)}
          setDiagnosticChargePoint={setDiagnosticChargePoint}
          onRequestDiagnostics={requestDiagnostics}
          onDownloadDiagnostics={downloadDiagnosticsToLocal}
        />
      )}

      {activeTab === "integrations" && (
        <WebhooksCard
          loading={loading}
          webhooks={webhooks}
          newWebhookUrl={newWebhookUrl}
          setNewWebhookUrl={setNewWebhookUrl}
          onCreateWebhook={createWebhook}
        />
      )}
    </Stack>
  );
}
